import { Event } from "../../events/Event";
import { Stat } from "../../utils/Stat";
import { Utils3D } from "../utils/Utils3D";
import { MeshRenderer } from "./MeshRenderer";
import { Sprite3D } from "./Sprite3D";
import { SkinnedMeshSprite3DShaderDeclaration } from "./SkinnedMeshSprite3DShaderDeclaration";
import { SkinRenderElement } from "./render/SkinRenderElement";
import { BlinnPhongMaterial } from "./material/BlinnPhongMaterial";
import { Bounds } from "../math/Bounds";
import { Vector3 } from "../../maths/Vector3";
import { Laya3DRender } from "../RenderObjs/Laya3DRender";
import { Vector4 } from "../../maths/Vector4";
import { Transform3D } from "./Transform3D";
import { BaseRenderType } from "../../RenderDriver/RenderModuleData/Design/3D/I3DRenderModuleData";
import { Shader3D } from "../../RenderEngine/RenderShader/Shader3D";
import { ShaderDataType } from "../../RenderDriver/DriverDesign/RenderDevice/ShaderData";
import { LayaGL } from "../../layagl/LayaGL";
import { Browser } from "../../utils/Browser";
import { StatElement } from "../../layagl/StatisticsContext";
export class SkinnedMeshRenderer extends MeshRenderer {
    static __init__() {
        SkinnedMeshSprite3DShaderDeclaration.SHADERDEFINE_BONE = Shader3D.getDefineByName("BONE");
        SkinnedMeshSprite3DShaderDeclaration.SHADERDEFINE_SIMPLEBONE = Shader3D.getDefineByName("SIMPLEBONE");
        const commandUniform = LayaGL.renderDeviceFactory.createGlobalUniformMap("SkinSprite3D");
        SkinnedMeshRenderer.BONES = Shader3D.propertyNameToID("u_Bones");
        commandUniform.addShaderUniformArray(SkinnedMeshRenderer.BONES, "u_Bones", ShaderDataType.Matrix4x4, 24);
    }
    get _bones() {
        return this.__bones;
    }
    set _bones(value) {
        this.__bones = value;
        this._isISkinRenderNode() && this._ownerSkinRenderNode.setBones(value);
    }
    get localBounds() {
        return this._localBounds;
    }
    set localBounds(value) {
        this._localBounds = value;
        this.geometryBounds = this._localBounds;
    }
    get rootBone() {
        return this._cacheRootBone;
    }
    set rootBone(value) {
        if (this._cacheRootBone != value) {
            if (this._cacheRootBone)
                this._cacheRootBone.transform.off(Event.TRANSFORM_CHANGED, this, this._onWorldMatNeedChange);
            else
                this.owner.transform.off(Event.TRANSFORM_CHANGED, this, this._onWorldMatNeedChange);
            if (value) {
                value.transform.on(Event.TRANSFORM_CHANGED, this, this._onWorldMatNeedChange);
                this._baseRenderNode.transform = value.transform;
            }
            else {
                this.owner.transform.on(Event.TRANSFORM_CHANGED, this, this._onWorldMatNeedChange);
                this._baseRenderNode.transform = this.owner.transform;
            }
            this._cacheRootBone = value;
            this._onWorldMatNeedChange(Transform3D.TRANSFORM_WORLDPOSITION | Transform3D.TRANSFORM_WORLDQUATERNION | Transform3D.TRANSFORM_WORLDSCALE);
            let count = this._renderElements.length;
            for (var i = 0; i < count; i++) {
                var renderElement = this._renderElements[i];
                renderElement.setTransform(value.transform);
            }
            this._isISkinRenderNode() && this._ownerSkinRenderNode.setRootBoneTransfom(this._cacheRootBone);
        }
        this._baseRenderNode.transform = this.rootBone ? this.rootBone.transform : this.owner.transform;
    }
    get bones() {
        return this._bones;
    }
    set bones(value) {
        this._bones = value;
    }
    constructor() {
        super();
        this.__bones = [];
        this._worldParams = new Vector4();
        this.localBounds = new Bounds(Vector3.ZERO, Vector3.ZERO);
        this._baseRenderNode.shaderData.addDefine(SkinnedMeshSprite3DShaderDeclaration.SHADERDEFINE_BONE);
        this._baseRenderNode.renderNodeType = BaseRenderType.SkinnedMeshRender;
    }
    _createBaseRenderNode() {
        this._ownerSkinRenderNode = Laya3DRender.Render3DModuleDataFactory.createSkinRenderNode();
        return this._ownerSkinRenderNode;
    }
    _getcommonUniformMap() {
        return ["Sprite3D", "SkinSprite3D"];
    }
    _needRender(boundFrustum, context) {
        if (!Stat.enableSkin)
            return false;
        return super._needRender(boundFrustum, context);
    }
    _createRenderElement() {
        let renderelement = new SkinRenderElement();
        return renderelement;
    }
    _isISkinRenderNode() {
        return this._ownerSkinRenderNode.setCacheMesh;
    }
    _onSkinMeshChange(mesh) {
        if (mesh && this._mesh != mesh) {
            this._changeVertexDefine(mesh);
            this._changeMorphData(mesh);
            this._mesh = mesh;
            this._isISkinRenderNode() && this._ownerSkinRenderNode.setCacheMesh(mesh);
            var count = mesh.subMeshCount;
            this._renderElements.length = count;
            let materials = this.sharedMaterials;
            materials.length = count;
            for (var i = 0; i < count; i++) {
                let renderElement = this._renderElements[i];
                if (!renderElement) {
                    renderElement = this._renderElements[i] = this._createRenderElement();
                    if (this._cacheRootBone) {
                        renderElement.setTransform(this._cacheRootBone.transform);
                    }
                    else {
                        renderElement.setTransform(this.owner._transform);
                    }
                    renderElement.render = this;
                }
                materials[i] = materials[i] || BlinnPhongMaterial.defaultMaterial;
                renderElement.setGeometry(mesh.getSubMesh(i));
            }
            this.sharedMaterials = materials;
            this.boundsChange = true;
        }
        else if (!mesh) {
            this._renderElements.length = 0;
            this._mesh = null;
            this._changeVertexDefine(null);
            this._changeMorphData(null);
            this.boundsChange = false;
        }
        this._meshChange = true;
    }
    _onMeshChange(value) {
        this._onSkinMeshChange(value);
        this._setRenderElements();
        if (!value)
            return;
        this._cacheMesh = value;
        var subMeshCount = value.subMeshCount;
        this._skinnedData = [];
        for (var i = 0; i < subMeshCount; i++) {
            var subBoneIndices = value.getSubMesh(i)._boneIndicesList;
            var subCount = subBoneIndices.length;
            var subData = this._skinnedData[i] = [];
            for (var j = 0; j < subCount; j++)
                subData[j] = new Float32Array(subBoneIndices[j].length * 16);
            this._renderElements[i].setSkinData(subData);
        }
        this._isISkinRenderNode() && this._ownerSkinRenderNode.setSkinnedData(this._skinnedData);
    }
    _setBelongScene(scene) {
        super._setBelongScene(scene);
        this._isISkinRenderNode() && this._ownerSkinRenderNode.setOwnerTransform(this.owner);
    }
    _setUnBelongScene() {
        super._setUnBelongScene();
    }
    _statAdd() {
        super._statAdd();
        LayaGL.statAgent.recordCountData(StatElement.C_SkinnedMeshRenderCount, 1);
    }
    _statRemove() {
        super._statRemove();
        LayaGL.statAgent.recordCountData(StatElement.C_SkinnedMeshRenderCount, -1);
    }
    renderUpdate(context) {
        let t = Browser.now();
        super.renderUpdate(context);
        this._isISkinRenderNode() && this._ownerSkinRenderNode.computeSkinnedData();
        LayaGL.statAgent.recordTimeData(StatElement.T_SkinBoneUpdate, Browser.now() - t);
    }
    _cloneTo(dest) {
        let getCommomParent = (rootNode, rootCheckNode) => {
            let nodeArray = [];
            let node = rootNode;
            while (!!node) {
                if (node instanceof Sprite3D)
                    nodeArray.push(node);
                node = node.parent;
            }
            let checkNode = rootCheckNode;
            while (!!checkNode && nodeArray.indexOf(checkNode) == -1) {
                checkNode = checkNode.parent;
            }
            return checkNode;
        };
        let cloneHierachFun = (rootNode, rootCheckNode, destNode) => {
            let rootparent = getCommomParent(rootNode, rootCheckNode);
            if (!rootparent)
                return null;
            let path = [];
            Utils3D._getHierarchyPath(rootparent, rootNode, path);
            let pathcheck = [];
            Utils3D._getHierarchyPath(rootparent, rootCheckNode, pathcheck);
            let destParent = Utils3D._getParentNodeByHierarchyPath(destNode, path);
            if (!destParent)
                return null;
            return Utils3D._getNodeByHierarchyPath(destParent, pathcheck);
        };
        var rootBone = this.rootBone;
        if (rootBone) {
            let node = cloneHierachFun(this.owner, this.rootBone, dest.owner);
            if (node)
                dest.rootBone = node;
            else
                dest.rootBone = rootBone;
        }
        var bones = this.bones;
        var destBone = dest.bones;
        let n = destBone.length = bones.length;
        for (var i = 0; i < n; i++) {
            let ceckNode = bones[i];
            destBone[i] = cloneHierachFun(this.owner, ceckNode, dest.owner);
        }
        dest.bones = dest.bones;
        var lbb = this.localBounds;
        (lbb) && (lbb.cloneTo(dest.localBounds));
        (dest.localBounds) && (dest.localBounds = dest.localBounds);
        super._cloneTo(dest);
    }
    _onDestroy() {
        if (this._cacheRootBone)
            (!this._cacheRootBone._destroyed) && (this._cacheRootBone.transform.off(Event.TRANSFORM_CHANGED, this, this._onWorldMatNeedChange));
        else
            (this.owner && !this.owner._destroyed) && (this.owner.transform.off(Event.TRANSFORM_CHANGED, this, this._onWorldMatNeedChange));
        super._onDestroy();
    }
}
